/**
 * Tracks every route change: sets current page (page_id, page_name) and sends
 * "Page Opened", route change, and time spent. So you always know which page
 * is open and which action was taken on it.
 */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { trackPageView, trackRouteChange, trackTimeSpent } from './behaviorTracking'
import { useTrackingContextStore } from './trackingContextStore'
import { onRouteChangeForScroll } from './behaviorListeners'

/** Human-readable page names for reports ("Orders List", "Order Details #123"). */
const PAGE_NAMES: Record<string, string> = {
  '': 'Dashboard',
  dashboard: 'Dashboard',
  orders: 'Orders List',
  'create-order': 'Create Order',
  providers: 'Providers List',
  financials: 'Financials',
  'account-settings': 'Account Settings',
  users: 'User Management',
  notifications: 'Notifications',
  login: 'Login',
  register: 'Register',
  'verify-otp': 'Verify OTP',
  'provider-registration': 'Provider Registration',
  'access-denied': 'Access Denied',
}

function getPageId(pathname: string): string {
  const segments = pathname.replace(/^\//, '').split('/').filter(Boolean)
  const first = segments[0] ?? 'home'
  if (first === 'orders' && segments[1]) return 'order-details'
  if (first === 'create-order' && segments[1]) return 'create-order-step'
  return first
}

function getPageName(pathname: string): string {
  const segments = pathname.replace(/^\//, '').split('/').filter(Boolean)
  const first = segments[0] ?? ''
  const baseName = PAGE_NAMES[first] ?? first.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

  if (first === 'orders' && segments[1]) return `Order Details #${segments[1]}`
  if (first === 'create-order' && segments[1]) return `Create Order - ${segments[1]}`

  return baseName
}

export function BehaviorRouteTracker() {
  const location = useLocation()
  const prevPathRef = useRef<string>(location.pathname)
  const enterTimeRef = useRef<number>(Date.now())
  const setFeatureFromPath = useTrackingContextStore((s) => s.setFeatureFromPath)
  const setPage = useTrackingContextStore((s) => s.setPage)

  useEffect(() => {
    const pathname = location.pathname
    const now = Date.now()
    const page_id = getPageId(pathname)
    const pageName = getPageName(pathname)

    // Time spent on previous screen (only when path actually changes)
    const prevPath = prevPathRef.current
    if (prevPath !== pathname) {
      const seconds = (now - enterTimeRef.current) / 1000
      if (seconds >= 0.5) {
        const prevPageName = getPageName(prevPath)
        trackTimeSpent(prevPath, prevPageName, Math.round(seconds * 10) / 10)
      }
      trackRouteChange(prevPath, pathname, pageName, { page_id })
      onRouteChangeForScroll(pathname)
    }

    setFeatureFromPath(pathname)
    setPage(page_id, pageName)
    prevPathRef.current = pathname
    enterTimeRef.current = now

    trackPageView(pageName, pathname, {
      page_id,
      extra: { search: location.search, title: document.title },
    })

    // Cleanup: on unmount send time spent for current screen
    return () => {
      const exitTime = Date.now()
      const seconds = (exitTime - enterTimeRef.current) / 1000
      if (seconds >= 0.5) {
        trackTimeSpent(pathname, pageName, Math.round(seconds * 10) / 10)
      }
    }
  }, [location.pathname, location.search, setFeatureFromPath, setPage])

  return null
}
